import { useEffect, useState } from "react";
import { ClipboardCheck, FileCheck2, Plus, Trash2, Paperclip, Download, Loader2, Link2, ShieldCheck, Sparkles, ListChecks, Camera } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Markdown } from "@/components/Markdown";

const TIPI_AUDIT = ["interno", "esterno", "sorveglianza", "certificazione", "rinnovo"];
const ESITO = {
  conforme: { label: "Conforme", cls: "bg-green-100 text-green-800 border-green-200" },
  osservazione: { label: "Osservazione", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  nc_minore: { label: "NC minore", cls: "bg-orange-100 text-orange-800 border-orange-200" },
  nc_maggiore: { label: "NC maggiore", cls: "bg-red-100 text-red-800 border-red-200" },
  raccomandazione: { label: "Raccomandazione", cls: "bg-blue-100 text-blue-800 border-blue-200" },
};
const currentYear = new Date().getFullYear();

function FileField({ file, onFile, disabled }) {
  return (
    <div className="mt-1.5 space-y-2">
      <label className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2.5 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors">
        <input type="file" className="hidden" disabled={disabled} onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid="audit-file-input" />
        <Paperclip className="h-4 w-4 text-slate-500" />
        <span className="text-sm text-slate-600 truncate">{file ? file.name : "Allega un documento (opzionale)"}</span>
      </label>
      <label className="flex items-center justify-center gap-2 h-11 rounded-md border border-indigo-400/40 bg-indigo-500/10 text-indigo-200 cursor-pointer hover:bg-indigo-500/20 hover:text-white transition-colors">
        <input type="file" accept="image/*" capture="environment" className="hidden" disabled={disabled}
          onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid="audit-camera-input" />
        <Camera className="h-4 w-4" />
        <span className="text-sm font-medium">Scatta una foto</span>
      </label>
    </div>
  );
}

function orgName(orgs, id) { return orgs.find((o) => o.id === id)?.nome || "Cliente"; }

export default function Audits() {
  const { tenantId, organizations, currentOrg } = useTenant();
  const { user } = useAuth();
  const isConsultant = user?.role === "consultant";
  const [plans, setPlans] = useState([]);
  const [reports, setReports] = useState([]);
  const [planOpen, setPlanOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [planForm, setPlanForm] = useState({ tenant_id: "", tipo: "interno", anno: currentYear, note: "" });
  const [reportForm, setReportForm] = useState({ tenant_id: "", riferimento_piano: "", note_esiti: "" });
  const [planFile, setPlanFile] = useState(null);
  const [reportFile, setReportFile] = useState(null);
  const [evidences, setEvidences] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidenceForm, setEvidenceForm] = useState({ tenant_id: "", report_id: "", descrizione: "", punto_norma: "", documento_id: "", esito: "conforme", data_rilievo: "", rilevato_da: "" });
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evReqs, setEvReqs] = useState([]);
  const [ncOpen, setNcOpen] = useState(false);
  const [ncText, setNcText] = useState("");
  const [ncLoading, setNcLoading] = useState(false);
  const [caList, setCaList] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const CA_STATO = { aperta: { l: "Aperta", c: "bg-amber-100 text-amber-800 border-amber-200" }, in_corso: { l: "In corso", c: "bg-blue-100 text-blue-800 border-blue-200" }, chiusa: { l: "Chiusa", c: "bg-green-100 text-green-800 border-green-200" } };
  const CA_PRIO = { alta: { l: "Alta", c: "bg-red-100 text-red-700 border-red-200" }, media: { l: "Media", c: "bg-orange-100 text-orange-700 border-orange-200" }, bassa: { l: "Bassa", c: "bg-slate-100 text-slate-600 border-slate-200" } };

  const generateActions = async () => {
    if (!tenantId) { toast.error("Seleziona un cliente in alto"); return; }
    try { const r = await api.post(`/clients/${tenantId}/nc-actions/generate`); toast.success(`${r.data.created} azioni correttive create${r.data.skipped ? `, ${r.data.skipped} già esistenti` : ""}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const updateActionStato = async (a, stato) => { try { await api.put(`/corrective-actions/${a.id}`, { stato }); load(); } catch (e) { toast.error(apiError(e)); } };
  const delAction = async (id) => { try { await api.delete(`/corrective-actions/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  const runNcPlan = async () => {
    if (!tenantId) { toast.error("Seleziona un cliente in alto per generare il piano"); return; }
    setNcOpen(true); setNcLoading(true); setNcText("");
    try {
      const r = await api.post(`/clients/${tenantId}/nc-action-plan`);
      setNcText(r.data.result || "");
    } catch (e) { toast.error(apiError(e)); setNcText("Errore nella generazione del piano."); }
    finally { setNcLoading(false); }
  };

  const load = () => {
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/audit-plans${p}`).then((r) => setPlans(r.data)).catch(() => {});
    api.get(`/audit-reports${p}`).then((r) => setReports(r.data)).catch(() => {});
    api.get(`/audit-evidences${p}`).then((r) => setEvidences(r.data)).catch(() => {});
    api.get(`/documents${p}`).then((r) => setDocuments(r.data)).catch(() => {});
    api.get(`/corrective-actions${p}`).then((r) => setCaList(r.data)).catch(() => {});
  };
  useEffect(load, [tenantId]);

  const uploadFile = async (tid, f) => {
    const fd = new FormData();
    fd.append("tenant_id", tid);
    fd.append("file", f);
    const r = await api.post("/audit-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return {
      storage_path: r.data.storage_path, original_filename: r.data.original_filename,
      content_type: r.data.content_type, size: r.data.size,
    };
  };

  const openPlan = () => { setPlanForm({ tenant_id: tenantId || "", tipo: "interno", anno: currentYear, note: "" }); setPlanFile(null); setPlanOpen(true); };
  const openReport = () => { setReportForm({ tenant_id: tenantId || "", riferimento_piano: "", note_esiti: "" }); setReportFile(null); setReportOpen(true); };

  const savePlan = async () => {
    if (!planForm.tenant_id) { toast.error("Seleziona un cliente"); return; }
    setSaving(true);
    try {
      const file = planFile ? await uploadFile(planForm.tenant_id, planFile) : null;
      await api.post("/audit-plans", { ...planForm, anno: Number(planForm.anno) || currentYear, file });
      toast.success("Piano di audit creato"); setPlanOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveReport = async () => {
    if (!reportForm.tenant_id) { toast.error("Seleziona un cliente"); return; }
    setSaving(true);
    try {
      const file = reportFile ? await uploadFile(reportForm.tenant_id, reportFile) : null;
      await api.post("/audit-reports", {
        ...reportForm, riferimento_piano: reportForm.riferimento_piano || null, file,
      });
      toast.success("Rapporto di audit creato"); setReportOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const delPlan = async (id) => { await api.delete(`/audit-plans/${id}`); load(); };
  const delReport = async (id) => { await api.delete(`/audit-reports/${id}`); load(); };

  const openEvidence = (report) => {
    setEvidenceForm({ tenant_id: report.tenant_id, report_id: report.id, descrizione: "", punto_norma: "", documento_id: "", esito: "conforme", data_rilievo: new Date().toISOString().slice(0, 10), rilevato_da: "" });
    setEvidenceFile(null); setEvidenceOpen(true);
    setEvReqs([]);
    api.get(`/clients/${report.tenant_id}/requirements`).then((r) => {
      const flat = [];
      (r.data.schemes || []).forEach((g) => g.requirements.forEach((rq) => flat.push({ ...rq, sigla: g.sigla })));
      setEvReqs(flat);
    }).catch(() => setEvReqs([]));
  };
  const saveEvidence = async () => {
    if (!evidenceForm.descrizione.trim()) { toast.error("Descrivi l'evidenza"); return; }
    setSaving(true);
    try {
      const file = evidenceFile ? await uploadFile(evidenceForm.tenant_id, evidenceFile) : null;
      await api.post("/audit-evidences", { ...evidenceForm, documento_id: evidenceForm.documento_id || null, data_rilievo: evidenceForm.data_rilievo || null, file });
      toast.success("Evidenza registrata"); setEvidenceOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const delEvidence = async (id) => { await api.delete(`/audit-evidences/${id}`); load(); };
  const docLabel = (id) => { const d = documents.find((x) => x.id === id); return d ? `${d.codice} — ${d.titolo}` : null; };

  const plansForReport = plans.filter((p) => p.tenant_id === reportForm.tenant_id);

  return (
    <div className="space-y-6">
      <PageHero label="Audit & verifiche" title="Piani e rapporti di audit"
        subtitle={tenantId ? <>Programma verifiche ed esiti per <strong>{currentOrg?.nome}</strong>.</> : "Programma verifiche e registra gli esiti per ogni cliente."} />

      {isConsultant && (
        <div className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid="nc-plan-banner">
          <span className="h-10 w-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-800">Chiedi a Steve: da dove partire per chiudere le non conformità</div>
            <div className="text-sm text-slate-600">Steve analizza le NC e le osservazioni degli audit e propone un piano di chiusura prioritizzato.</div>
          </div>
          <Button onClick={runNcPlan} disabled={ncLoading} data-testid="nc-plan-btn"
            className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white shrink-0">
            {ncLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Genera piano NC
          </Button>
        </div>
      )}

      <div className="rounded-2xl p-5 bg-gradient-to-br from-[#0A1734] via-[#0A1027] to-[#06090F] border border-white/10 shadow-xl relative overflow-hidden" data-testid="audits-deck">
        <div className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-indigo-600/20 blur-3xl" />
        <div className="relative flex items-center gap-2 mb-4">
          <span className="h-2 w-2 rounded-full bg-cyan-400 shadow-[0_0_8px_2px_rgba(34,211,238,.5)]" />
          <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-400">Panoramica audit</span>
        </div>
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Piani", value: plans.length, accent: "text-white" },
            { label: "Rapporti", value: reports.length, accent: "text-white" },
            { label: "Evidenze", value: evidences.length, accent: "text-white" },
            { label: "Azioni aperte", value: caList.filter((a) => a.stato !== "chiusa").length, accent: "text-amber-300" },
          ].map((k, i) => (
            <div key={i} className="rounded-xl bg-white/[0.05] border border-white/10 p-4" data-testid={`audit-deck-${i}`}>
              <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{k.label}</div>
              <div className={`font-display text-3xl font-bold mt-1.5 ${k.accent}`}>{k.value}</div>
            </div>
          ))}
        </div>
      </div>

      <Tabs defaultValue="plans">
        <TabsList data-testid="audit-tabs" className="w-full overflow-x-auto justify-start">
          <TabsTrigger value="plans" data-testid="tab-plans"><ClipboardCheck className="h-4 w-4 mr-2" /> Piani ({plans.length})</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><FileCheck2 className="h-4 w-4 mr-2" /> Rapporti ({reports.length})</TabsTrigger>
          <TabsTrigger value="evidences" data-testid="tab-evidences"><ShieldCheck className="h-4 w-4 mr-2" /> Evidenze ({evidences.length})</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions"><ListChecks className="h-4 w-4 mr-2" /> Azioni ({caList.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="mt-4 space-y-4">
          {isConsultant && (
            <div className="flex justify-end">
              <Button onClick={openPlan} data-testid="new-plan-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                <Plus className="h-4 w-4 mr-2" /> Nuovo piano
              </Button>
            </div>
          )}
          {plans.length === 0 ? (
            <Card className="p-12 text-center border-dashed" data-testid="no-plans">
              <ClipboardCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessun piano di audit registrato.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="plans-list">
              {plans.map((p) => (
                <Card key={p.id} className="p-5 border-slate-200" data-testid={`plan-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 capitalize">{p.tipo}</Badge>
                        <span className="font-display text-lg font-semibold">{p.anno}</span>
                      </div>
                      {!tenantId && <div className="text-xs text-slate-500 mt-1">{orgName(organizations, p.tenant_id)}</div>}
                      {p.note && <p className="text-sm text-slate-600 mt-2">{p.note}</p>}
                    </div>
                    {isConsultant && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => delPlan(p.id)} data-testid={`plan-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                  {p.file?.storage_path && (
                    <a href={fileUrl(p.file.storage_path)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline" data-testid={`plan-file-${p.id}`}>
                      <Download className="h-4 w-4" /> {p.file.original_filename || "Allegato"}
                    </a>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reports" className="mt-4 space-y-4">
          {isConsultant && (
            <div className="flex justify-end">
              <Button onClick={openReport} data-testid="new-report-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                <Plus className="h-4 w-4 mr-2" /> Nuovo rapporto
              </Button>
          </div>
          )}
          {reports.length === 0 ? (
            <Card className="p-12 text-center border-dashed" data-testid="no-reports">
              <FileCheck2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessun rapporto di audit registrato.</p>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="reports-list">
              {reports.map((r) => {
                const plan = plans.find((p) => p.id === r.riferimento_piano);
                return (
                  <Card key={r.id} className="p-5 border-slate-200" data-testid={`report-${r.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <FileCheck2 className="h-4 w-4 text-indigo-600" />
                          <span className="font-medium">Rapporto di audit</span>
                          {plan && <Badge variant="outline" className="text-[11px] capitalize">{plan.tipo} {plan.anno}</Badge>}
                        </div>
                        {!tenantId && <div className="text-xs text-slate-500 mt-1">{orgName(organizations, r.tenant_id)}</div>}
                        {r.note_esiti && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{r.note_esiti}</p>}
                      </div>
                      {isConsultant && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => delReport(r.id)} data-testid={`report-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                    {r.file?.storage_path && (
                      <a href={fileUrl(r.file.storage_path)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline" data-testid={`report-file-${r.id}`}>
                        <Download className="h-4 w-4" /> {r.file.original_filename || "Allegato"}
                      </a>
                    )}
                    {(() => {
                      const evs = evidences.filter((e) => e.report_id === r.id);
                      return (
                        <div className="mt-4 border-t border-slate-100 pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Evidenze tracciate ({evs.length})</span>
                            {isConsultant && (
                              <Button size="sm" variant="outline" className="h-7 text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => openEvidence(r)} data-testid={`add-evidence-${r.id}`}>
                                <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi evidenza
                              </Button>
                            )}
                          </div>
                          {evs.length === 0 ? (
                            <p className="text-xs text-slate-400">Nessuna evidenza registrata per questo audit.</p>
                          ) : (
                            <div className="space-y-2" data-testid={`evidences-${r.id}`}>
                              {evs.map((e) => (
                                <div key={e.id} className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2" data-testid={`evidence-${e.id}`}>
                                  <Badge className={`${ESITO[e.esito]?.cls || ""} shrink-0`}>{ESITO[e.esito]?.label || e.esito}</Badge>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-slate-700">{e.descrizione}</p>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                                      {e.punto_norma && <span>Punto norma: <strong>{e.punto_norma}</strong></span>}
                                      {e.documento_id && docLabel(e.documento_id) && <span className="inline-flex items-center gap-1"><Link2 className="h-3 w-3" />{docLabel(e.documento_id)}</span>}
                                      {e.data_rilievo && <span>{e.data_rilievo}</span>}
                                      {e.rilevato_da && <span>Rilevato da: {e.rilevato_da}</span>}
                                      {e.file?.storage_path && <a href={fileUrl(e.file.storage_path)} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1"><Download className="h-3 w-3" />allegato</a>}
                                    </div>
                                  </div>
                                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 shrink-0" onClick={() => delEvidence(e.id)} data-testid={`evidence-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="evidences" className="mt-4 space-y-3">
          {evidences.length === 0 ? (
            <Card className="p-12 text-center border-dashed" data-testid="no-evidences">
              <ShieldCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessuna evidenza tracciata. Aprila da un rapporto di audit con "Aggiungi evidenza".</p>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="all-evidences">
              {evidences.map((e) => (
                <Card key={e.id} className="p-3 flex items-start gap-3 border-slate-200" data-testid={`ev-${e.id}`}>
                  <Badge className={`${ESITO[e.esito]?.cls || ""} shrink-0`}>{ESITO[e.esito]?.label || e.esito}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">{e.descrizione}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                      {!tenantId && <span>{orgName(organizations, e.tenant_id)}</span>}
                      {e.punto_norma && <span>Punto norma: <strong>{e.punto_norma}</strong></span>}
                      {e.documento_id && docLabel(e.documento_id) && <span className="inline-flex items-center gap-1"><Link2 className="h-3 w-3" />{docLabel(e.documento_id)}</span>}
                      {e.data_rilievo && <span>{e.data_rilievo}</span>}
                      {e.rilevato_da && <span>Rilevato da: {e.rilevato_da}</span>}
                      {e.file?.storage_path && <a href={fileUrl(e.file.storage_path)} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1"><Download className="h-3 w-3" />allegato</a>}
                    </div>
                  </div>
                  {isConsultant && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 shrink-0" onClick={() => delEvidence(e.id)} data-testid={`ev-del-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="actions" className="mt-4 space-y-3">
          {isConsultant && (
            <div className="flex justify-end">
              <Button onClick={generateActions} data-testid="gen-actions-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                <Sparkles className="h-4 w-4 mr-2" /> Genera azioni dalle NC
              </Button>
            </div>
          )}
          {caList.length === 0 ? (
            <Card className="p-12 text-center border-dashed" data-testid="no-actions">
              <ListChecks className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessuna azione correttiva. Generale dalle non conformità o dal piano di Steve.</p>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="all-actions">
              {caList.map((a) => {
                const late = a.data_scadenza && a.stato !== "chiusa" && a.data_scadenza < today;
                return (
                  <Card key={a.id} className="p-3 flex items-start gap-3 border-slate-200" data-testid={`ca-${a.id}`}>
                    <Badge className={`${CA_PRIO[a.priorita]?.c || ""} shrink-0`}>{CA_PRIO[a.priorita]?.l || a.priorita}</Badge>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${a.stato === "chiusa" ? "line-through text-slate-400" : "text-slate-700"}`}>{a.titolo}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                        {!tenantId && <span>{orgName(organizations, a.tenant_id)}</span>}
                        {a.source_type === "incidente" && <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px]">Da incidente</Badge>}
                        {a.punto_norma && <span>Punto norma: <strong>{a.punto_norma}</strong></span>}
                        {a.data_scadenza && <span className={late ? "text-red-600 font-medium" : ""}>Scadenza: {a.data_scadenza}{late ? " (scaduta)" : ""}</span>}
                        {a.responsabile && <span>Responsabile: {a.responsabile}</span>}
                      </div>
                    </div>
                    {isConsultant ? (
                      <>
                        <Select value={a.stato} onValueChange={(v) => updateActionStato(a, v)}>
                          <SelectTrigger className="h-8 w-32 shrink-0" data-testid={`ca-stato-${a.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="aperta">Aperta</SelectItem>
                            <SelectItem value="in_corso">In corso</SelectItem>
                            <SelectItem value="chiusa">Chiusa</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 shrink-0" onClick={() => delAction(a.id)} data-testid={`ca-del-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </>
                    ) : (
                      <Badge className={`${CA_STATO[a.stato]?.c || ""} shrink-0`}>{CA_STATO[a.stato]?.l || a.stato}</Badge>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Plan dialog */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo piano di audit</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Cliente</Label>
              <Select value={planForm.tenant_id} onValueChange={(v) => setPlanForm({ ...planForm, tenant_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="plan-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={planForm.tipo} onValueChange={(v) => setPlanForm({ ...planForm, tipo: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="plan-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPI_AUDIT.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Anno</Label><Input type="number" value={planForm.anno} data-testid="plan-anno" onChange={(e) => setPlanForm({ ...planForm, anno: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Note</Label><Textarea value={planForm.note} data-testid="plan-note" onChange={(e) => setPlanForm({ ...planForm, note: e.target.value })} rows={3} className="mt-1.5" /></div>
            <div><Label>Allegato</Label><FileField file={planFile} onFile={setPlanFile} disabled={saving} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanOpen(false)}>Annulla</Button>
            <Button onClick={savePlan} disabled={saving} data-testid="save-plan-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo rapporto di audit</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Cliente</Label>
              <Select value={reportForm.tenant_id} onValueChange={(v) => setReportForm({ ...reportForm, tenant_id: v, riferimento_piano: "" })}>
                <SelectTrigger className="mt-1.5" data-testid="report-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Piano di riferimento (opzionale)</Label>
              <Select value={reportForm.riferimento_piano || "none"} onValueChange={(v) => setReportForm({ ...reportForm, riferimento_piano: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="report-plan"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {plansForReport.map((p) => <SelectItem key={p.id} value={p.id} className="capitalize">{p.tipo} {p.anno}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Esiti / note</Label><Textarea value={reportForm.note_esiti} data-testid="report-note" onChange={(e) => setReportForm({ ...reportForm, note_esiti: e.target.value })} rows={4} className="mt-1.5" /></div>
            <div><Label>Allegato</Label><FileField file={reportFile} onFile={setReportFile} disabled={saving} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Annulla</Button>
            <Button onClick={saveReport} disabled={saving} data-testid="save-report-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evidence dialog */}
      <Dialog open={evidenceOpen} onOpenChange={setEvidenceOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuova evidenza di audit</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Descrizione dell'evidenza</Label><Textarea value={evidenceForm.descrizione} data-testid="evidence-descrizione" onChange={(e) => setEvidenceForm({ ...evidenceForm, descrizione: e.target.value })} rows={3} className="mt-1.5" placeholder="Cosa è stato verificato e con quale riscontro" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Esito</Label>
                <Select value={evidenceForm.esito} onValueChange={(v) => setEvidenceForm({ ...evidenceForm, esito: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="evidence-esito"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(ESITO).map((k) => <SelectItem key={k} value={k}>{ESITO[k].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Punto norma</Label>
                <Select value={evidenceForm.punto_norma || "none"} onValueChange={(v) => setEvidenceForm({ ...evidenceForm, punto_norma: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="evidence-punto-norma"><SelectValue placeholder="Seleziona punto norma" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">Nessuno</SelectItem>
                    {evReqs.map((r) => (
                      <SelectItem key={r.id} value={`${r.sigla} ${r.codice}`} data-testid={`ev-req-${r.id}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className="shrink-0 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">{r.sigla}</span>
                          <span className="font-mono text-xs">{r.codice}</span>
                          <span className="text-slate-600">{r.titolo}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Documento collegato (opzionale)</Label>
              <Select value={evidenceForm.documento_id || "none"} onValueChange={(v) => setEvidenceForm({ ...evidenceForm, documento_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="evidence-documento"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {documents.filter((d) => d.tenant_id === evidenceForm.tenant_id).map((d) => <SelectItem key={d.id} value={d.id}>{d.codice} — {d.titolo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Data rilievo</Label><Input type="date" value={evidenceForm.data_rilievo} data-testid="evidence-data" onChange={(e) => setEvidenceForm({ ...evidenceForm, data_rilievo: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Rilevato da</Label><Input value={evidenceForm.rilevato_da} data-testid="evidence-auditor" onChange={(e) => setEvidenceForm({ ...evidenceForm, rilevato_da: e.target.value })} className="mt-1.5" placeholder="Auditor" /></div>
            </div>
            <div><Label>Allegato (verbale, foto, registrazione)</Label><FileField file={evidenceFile} onFile={setEvidenceFile} disabled={saving} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvidenceOpen(false)}>Annulla</Button>
            <Button onClick={saveEvidence} disabled={saving} data-testid="save-evidence-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Registra evidenza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ncOpen} onOpenChange={setNcOpen}>
        <DialogContent className="max-w-2xl" data-testid="nc-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-indigo-600" /> Piano di chiusura NC · Steve {currentOrg?.nome ? `· ${currentOrg.nome}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="nc-plan-content">
            {ncLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin mb-3" />
                Steve sta analizzando le non conformità…
              </div>
            ) : (
              <div className="prose prose-sm max-w-none"><Markdown>{ncText}</Markdown></div>
            )}
          </div>
          {!ncLoading && ncText && (
            <DialogFooter>
              <Button onClick={async () => { await generateActions(); setNcOpen(false); }} data-testid="save-nc-actions-btn"
                className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                <ListChecks className="h-4 w-4 mr-2" /> Salva come azioni correttive
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
